import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  NgZone,
  signal
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertService, TrackService } from '@pharvio/core/ui';

const INSTALL_DISMISSED_KEY = 'pwa_install_dismissed';
/** Manual-install hints (iOS etc.) — long-lived, we cannot see the outcome. */
const HELP_DISMISSED_KEY = 'pwa_install_help_dismissed';
/** Set by `appinstalled`; cleared when the browser offers install again. */
const INSTALLED_FLAG_KEY = 'pwa_installed';
const PROMPT_DELAY_MS = 2 * 60 * 1000; // 2 minutes
const COOLDOWN_MS = 60 * 60 * 1000; // re-prompt after 1 hour
const HELP_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // manual hint: weekly
/** Small-screen floater: closing it hides it for a few days, on every page. */
const FLOATER_DISMISSED_KEY = 'pwa_install_floater_dismissed';
const FLOATER_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;
/** Vex `md` breakpoint: below it the floater replaces the toolbar button. */
const SMALL_SCREEN_QUERY = '(max-width: 959.98px)';

export type PwaPlatform = 'ios' | 'android' | 'desktop';

/**
 * How this device can install the app right now.
 * - `installed`: running as the installed app, or the browser told us it is.
 * - `native`:    the browser handed us an install prompt (Chromium, Samsung).
 * - `manual`:    installable, but only through the browser's own menu
 *                (every iOS browser, Firefox on Android).
 * - `unavailable`: nothing to offer (e.g. desktop Firefox).
 */
export type PwaInstallMode = 'installed' | 'native' | 'manual' | 'unavailable';

export interface PwaInstallHelpData {
  platform: PwaPlatform;
  /** iOS only: Safari has the Share button in the toolbar; others in a menu. */
  iosSafari: boolean;
  /** Firefox words its menu item differently from other Android browsers. */
  firefox: boolean;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private readonly alertService = inject(AlertService);
  private readonly track = inject(TrackService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly installed = signal(false);
  private readonly promptReady = signal(false);
  private readonly smallScreen = signal(false);
  private readonly floaterDismissed = signal(false);

  readonly platform: PwaPlatform = detectPlatform();
  private readonly iosSafari = isIosSafari();

  /**
   * Chromium browsers (Chrome, Edge, Samsung Internet, Opera — desktop and
   * Android) expose this event and let us trigger the real install dialog.
   * Firefox and every iOS browser do not: there is no API to call, so the
   * walkthrough is the only thing we can offer them.
   */
  private readonly promptCapable = 'onbeforeinstallprompt' in window;

  readonly mode = computed<PwaInstallMode>(() => {
    if (this.installed()) return 'installed';
    if (this.promptReady()) return 'native';
    // A prompt-capable browser always installs through its own dialog. Until
    // the prompt arrives (or if it never does: already installed, or the
    // browser judged the app not installable) we offer nothing, rather than
    // a manual walkthrough the user does not need.
    if (this.promptCapable) return 'unavailable';
    // No install API at all: phones get the walkthrough, desktops nothing.
    return this.platform === 'desktop' ? 'unavailable' : 'manual';
  });
  readonly isInstalled = computed(() => this.mode() === 'installed');
  /** There is something we can offer the user on this device. */
  readonly canOfferInstall = computed(
    () => this.mode() === 'native' || this.mode() === 'manual'
  );
  /**
   * Phones and tablets use the floater at any width: a tablet in landscape is
   * wider than the breakpoint, and the toolbar button does not exist on the
   * sign-in pages, so width alone left it with nothing to tap.
   */
  private readonly usesFloater = computed(
    () => this.smallScreen() || this.platform !== 'desktop'
  );
  /** Bottom floater, on every page. */
  readonly showFloater = computed(
    () =>
      this.canOfferInstall() && this.usesFloater() && !this.floaterDismissed()
  );
  /** Desktop-class devices keep the toolbar button. */
  readonly showToolbarButton = computed(
    () => this.canOfferInstall() && !this.usesFloater()
  );

  init(): void {
    if (isStandalone()) {
      this.installed.set(true);
      // The only signal that installs are actually being used: `pwa_installed`
      // fires once, this fires on every session in the installed app.
      this.track.track({ name: 'pwa_launched_standalone', props: {} });
      return;
    }

    this.installed.set(readFlag(INSTALLED_FLAG_KEY));
    void this.checkRelatedApps();

    const media = window.matchMedia(SMALL_SCREEN_QUERY);
    const onMedia = () =>
      this.zone.run(() => this.smallScreen.set(media.matches));
    this.smallScreen.set(media.matches);
    media.addEventListener('change', onMedia);
    this.floaterDismissed.set(
      inCooldown(localStorage, FLOATER_DISMISSED_KEY, FLOATER_COOLDOWN_MS)
    );

    const handler = (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      // Chromium only offers install when the app is NOT installed, so this
      // also tells us a previous install was removed.
      clearFlag(INSTALLED_FLAG_KEY);
      this.zone.run(() => {
        this.installed.set(false);
        this.promptReady.set(true);
      });
      this.track.track({ name: 'pwa_install_prompt_available', props: {} });
    };

    // Fires whether the install came from our UI or the browser's own
    // affordance, so it's the only complete count of installs.
    const installedHandler = () => {
      writeFlag(INSTALLED_FLAG_KEY);
      this.deferredPrompt = null;
      this.zone.run(() => {
        this.installed.set(true);
        this.promptReady.set(false);
      });
      this.track.track({ name: 'pwa_installed', props: {} });
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
      media.removeEventListener('change', onMedia);
      if (this.timer) clearTimeout(this.timer);
    });

    // One timed nudge per session on every screen size; what it offers is
    // decided when it fires, once we know whether the browser gave a prompt.
    this.timer = setTimeout(
      () => this.zone.run(() => this.autoPrompt()),
      PROMPT_DELAY_MS
    );
  }

  /** Manual entry point (toolbar button): install now, or show how. */
  install(): void {
    switch (this.mode()) {
      case 'native':
        void this.runNativePrompt();
        break;
      case 'manual':
        this.openHelp('manual');
        break;
      default:
        break;
    }
  }

  /** Floater closed without installing: stay quiet for a few days. */
  dismissFloater(): void {
    this.floaterDismissed.set(true);
    safeSet(localStorage, FLOATER_DISMISSED_KEY, String(Date.now()));
    this.track.track({
      name: 'pwa_install_prompt_dismissed',
      props: { source: 'floater' }
    });
  }

  // ── Timed nudge ─────────────────────────────────────────────────────────

  private autoPrompt(): void {
    // These devices already carry the floater; a snackbar on top is noise.
    if (this.usesFloater()) return;
    const mode = this.mode();
    if (mode === 'installed') {
      this.track.track({
        name: 'pwa_install_prompt_suppressed',
        props: { reason: 'installed' }
      });
      return;
    }
    if (mode === 'native') {
      if (inCooldown(sessionStorage, INSTALL_DISMISSED_KEY, COOLDOWN_MS)) {
        this.track.track({
          name: 'pwa_install_prompt_suppressed',
          props: { reason: 'cooldown' }
        });
        return;
      }
      this.showNativeSnackbar();
      return;
    }
    if (mode === 'manual') {
      if (inCooldown(localStorage, HELP_DISMISSED_KEY, HELP_COOLDOWN_MS)) {
        this.track.track({
          name: 'pwa_install_prompt_suppressed',
          props: { reason: 'cooldown' }
        });
        return;
      }
      this.showManualSnackbar();
    }
  }

  private showNativeSnackbar(): void {
    if (!this.deferredPrompt) return;
    const ref = this.alertService.info({
      message: 'Install Pharvios for faster access and offline support.',
      buttonText: 'Install',
      cancelText: 'Not now',
      duration: 0
    });
    this.track.track({ name: 'pwa_install_prompt_shown', props: {} });

    ref?.afterDismissed().subscribe(({ dismissedByAction }) => {
      if (dismissedByAction) {
        void this.runNativePrompt();
      } else {
        this.track.track({
          name: 'pwa_install_prompt_dismissed',
          props: { source: 'snackbar' }
        });
      }
      safeSet(sessionStorage, INSTALL_DISMISSED_KEY, String(Date.now()));
    });
  }

  private showManualSnackbar(): void {
    const ref = this.alertService.info({
      message:
        'Add Pharvios to your home screen for faster access and offline support.',
      buttonText: 'Show me how',
      cancelText: 'Not now',
      duration: 0
    });
    this.track.track({ name: 'pwa_install_prompt_shown', props: {} });

    ref?.afterDismissed().subscribe(({ dismissedByAction }) => {
      if (dismissedByAction) {
        this.openHelp('auto');
      } else {
        this.track.track({
          name: 'pwa_install_prompt_dismissed',
          props: { source: 'snackbar' }
        });
      }
      safeSet(localStorage, HELP_DISMISSED_KEY, String(Date.now()));
    });
  }

  // ── Actions ─────────────────────────────────────────────────────────────

  /** Our UI only opens the browser's dialog — its outcome is the real result. */
  private async runNativePrompt(): Promise<void> {
    const prompt = this.deferredPrompt;
    if (!prompt) return;
    // A prompt event can be used once.
    this.deferredPrompt = null;
    this.promptReady.set(false);
    try {
      const { outcome } = await prompt.prompt();
      if (outcome === 'accepted') {
        this.track.track({ name: 'pwa_install_accepted', props: {} });
      } else {
        this.track.track({
          name: 'pwa_install_prompt_dismissed',
          props: { source: 'native' }
        });
      }
    } catch {
      // The browser refused to show it (e.g. no user gesture) — nothing to do.
    }
  }

  private openHelp(trigger: 'auto' | 'manual'): void {
    this.track.track({
      name: 'pwa_install_help_shown',
      props: { platform: this.platform, trigger }
    });
    const data: PwaInstallHelpData = {
      platform: this.platform,
      iosSafari: this.iosSafari,
      firefox: /Firefox\//.test(navigator.userAgent)
    };
    // Lazy: most sessions never open it.
    void import(
      '../components/pwa-install-help/pwa-install-help.component'
    ).then((m) =>
      this.dialog.open(m.PwaInstallHelpComponent, {
        data,
        width: '420px',
        maxWidth: '94vw'
      })
    );
  }

  /**
   * Chromium on Android can tell a browser tab that the app is installed,
   * provided the manifest lists itself under `related_applications`.
   */
  private async checkRelatedApps(): Promise<void> {
    const nav = navigator as NavigatorWithRelatedApps;
    if (typeof nav.getInstalledRelatedApps !== 'function') return;
    try {
      const apps = await nav.getInstalledRelatedApps();
      if (apps.length > 0) this.zone.run(() => this.installed.set(true));
    } catch {
      // Unsupported context — fall back to the other signals.
    }
  }
}

// ── Detection helpers ─────────────────────────────────────────────────────

function isStandalone(): boolean {
  const modes = [
    'standalone',
    'fullscreen',
    'minimal-ui',
    'window-controls-overlay'
  ];
  if (modes.some((m) => window.matchMedia(`(display-mode: ${m})`).matches)) {
    return true;
  }
  // iOS home-screen apps never match `display-mode`; this is their signal.
  return (navigator as NavigatorWithStandalone).standalone === true;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac; touch points give it away.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

function isIosSafari(): boolean {
  return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\//.test(navigator.userAgent);
}

function detectPlatform(): PwaPlatform {
  if (isIos()) return 'ios';
  if (/Android/.test(navigator.userAgent)) return 'android';
  // The user agent lies often enough to matter: "Request desktop site" on a
  // phone strips `Android`, and DevTools' responsive mode keeps the desktop
  // string. A touch-first device without an iOS signature installs through
  // the browser menu, which is exactly the Android walkthrough.
  if (isTouchFirst()) return 'android';
  return 'desktop';
}

function isTouchFirst(): boolean {
  const uaData = (navigator as NavigatorWithUaData).userAgentData;
  if (uaData?.mobile) return true;
  return (
    navigator.maxTouchPoints > 0 &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

function inCooldown(storage: Storage, key: string, ms: number): boolean {
  try {
    const at = storage.getItem(key);
    return !!at && Date.now() - Number(at) < ms;
  } catch {
    return false;
  }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Private mode / storage disabled.
  }
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  safeSet(localStorage, key, '1');
}

function clearFlag(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Chrome's non-standard event — not in lib.dom.d.ts. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface NavigatorWithUaData extends Navigator {
  userAgentData?: { mobile?: boolean };
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

interface NavigatorWithRelatedApps extends Navigator {
  getInstalledRelatedApps?: () => Promise<
    Array<{ platform: string; url?: string }>
  >;
}
